import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Menu, X, ChevronDown, LogOut, MapPin, Package, RefreshCw, User, Heart, Store, ShieldCheck, LayoutDashboard, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import CategoryAwareSearch from "@/components/search/CategoryAwareSearch";
import DeliverToSelector from "@/components/layout/DeliverToSelector";
import MobileNavMenu from "@/components/layout/MobileNavMenu";
import { BrandWordmark } from "@/components/brand/BrandWordmark";

const MarketplaceHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const { user, isVendor, isAdmin, signOut, loading, vendorInfo } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();

  const handleSignOut = async () => { await signOut(); };

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(mobileSearchQuery.trim())}`);
      setMobileSearchQuery("");
    }
  };

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <nav className="fabric-texture sticky top-0 z-40 min-h-[80px] md:min-h-[56px] lg:min-h-[64px]">
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex items-center justify-between h-10 md:h-14 lg:h-16 gap-2 md:gap-4">
            {/* Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="flex-shrink-0 h-8 w-8"
              onClick={() => setMenuOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={menuOpen}
              aria-controls="side-menu"
            >
              <Menu className="h-4 w-4" aria-hidden="true" />
            </Button>

            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <BrandWordmark tone="dark" className="h-7 md:h-10" title="Hijabi Store — Home" />
            </Link>

            

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-xl mx-4">
              <div className="w-full relative">
                <CategoryAwareSearch />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 md:gap-2">
              {/* Account */}
              {!loading && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="hidden md:flex items-center gap-1 h-auto py-1.5 px-3 text-xs uppercase tracking-widest font-semibold rounded-full bg-muted hover:bg-muted/80">
                      <span>{user ? "Account" : "Sign in"}</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 rounded-[14px]">
                    {user ? (
                      <>
                        <DropdownMenuItem asChild><Link to="/account" className="text-xs font-medium"><User className="h-3 w-3 mr-2" />My Account</Link></DropdownMenuItem>
                        <DropdownMenuItem asChild><Link to="/orders" className="text-xs font-medium"><Package className="h-3 w-3 mr-2" />Orders</Link></DropdownMenuItem>
                        <DropdownMenuItem asChild><Link to="/account/returns" className="text-xs font-medium"><RefreshCw className="h-3 w-3 mr-2" />Returns</Link></DropdownMenuItem>
                        <DropdownMenuItem asChild><Link to="/wishlist" className="text-xs font-medium"><Heart className="h-3 w-3 mr-2" />Wishlist</Link></DropdownMenuItem>
                        <DropdownMenuItem asChild><Link to="/account?tab=addresses" className="text-xs font-medium"><MapPin className="h-3 w-3 mr-2" />Deliver To</Link></DropdownMenuItem>
                        {isVendor && <DropdownMenuItem asChild><Link to="/vendor" className="text-xs font-medium"><LayoutDashboard className="h-3 w-3 mr-2" />Vendor Portal</Link></DropdownMenuItem>}
                        {!isVendor && <DropdownMenuItem asChild><Link to="/vendor/application" className="text-xs font-medium"><FileText className="h-3 w-3 mr-2" />Vendor Application</Link></DropdownMenuItem>}
                        {isAdmin && <DropdownMenuItem asChild><Link to="/admin" className="text-xs font-medium"><ShieldCheck className="h-3 w-3 mr-2" />Admin Panel</Link></DropdownMenuItem>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleSignOut} className="text-destructive text-xs font-medium">
                          <LogOut className="h-3 w-3 mr-2" /> Sign Out
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem asChild><Link to="/auth" className="text-xs font-medium">Sign In</Link></DropdownMenuItem>
                        <DropdownMenuItem asChild><Link to="/auth?mode=signup" className="text-xs font-medium">Create Account</Link></DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              

              {/* Sell on HJB pill */}
              <Link
                to={user && vendorInfo && vendorInfo.status !== "rejected" ? "/vendor/application" : "/vendor/register"}
                className="hidden md:inline-flex items-center border-[1.5px] border-[hsl(5,72%,58%)] text-[hsl(5,72%,58%)] bg-transparent hover:bg-[hsl(5,72%,58%)] hover:text-white rounded-[20px] px-[14px] py-[5px] text-xs font-medium transition-colors"
              >
                Sell on HJB
              </Link>

              {/* Cart */}
              <Button variant="ghost" className="hidden md:relative md:flex items-center gap-1.5 h-auto py-1.5 px-3" asChild>
                <Link to="/cart">
                  <ShoppingCart className="h-4 w-4" />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 left-6 bg-primary text-primary-foreground text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-semibold">
                      {itemCount > 9 ? "9+" : itemCount}
                    </span>
                  )}
                  <span className="hidden lg:inline text-xs uppercase tracking-widest font-semibold">Cart</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Mobile Search */}
          <div className="md:hidden pb-2">
            <form onSubmit={handleMobileSearch} className="flex">
              <Input type="search" placeholder="Search..." value={mobileSearchQuery} onChange={e => setMobileSearchQuery(e.target.value)} className="rounded-l-full rounded-r-none flex-1 h-9 text-xs border-border bg-muted" />
              <Button type="submit" className="rounded-l-none rounded-r-full bg-charcoal hover:bg-charcoal/90 h-9 px-4" aria-label="Search">
                <Search className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </form>
          </div>
        </div>
      </nav>

      {/* Side Panel Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Side Panel Menu */}
      <div
        id="side-menu"
        role="navigation"
        aria-label="Main navigation"
        className={`fixed top-0 left-0 z-50 h-full w-[75%] max-w-[320px] bg-background border-r border-border shadow-2xl transform transition-transform duration-300 ease-in-out ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <Link to="/" onClick={() => setMenuOpen(false)} className="flex-shrink-0">
            <BrandWordmark tone="dark" className="h-6" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation menu"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        <MobileNavMenu onClose={() => setMenuOpen(false)} onSignOut={handleSignOut} />
      </div>
    </>
  );
};

export default MarketplaceHeader;
